import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'

import FieldError from '@/components/form/FieldError'
import CardHeading from '@/components/misc/CommercialAgreementsForm/components/CardHeading'
import ReviewActions from '@/components/misc/CommercialAgreementsForm/components/ReviewActions'
import { ServiceFeeDetail } from '@/components/misc/ServiceFeesTable/ServiceFeesTableRows'
import ReviewESignatureLetterModal from '@/components/modals/ReviewESignatureLetterModal/ReviewESignatureLetterModal'
import { useReviewESignatureLetterModal } from '@/components/modals/ReviewESignatureLetterModal/useReviewESignatureLetterModal'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DOM_IDS } from '@/lib/constants'
import { faCheckCircle, faPenField } from '@/lib/fontAwesomePro/regular'
import { cn } from '@/lib/utils'
import { CommercialAgreementTypeOption } from '@/redux/slices/commercialAgreementsForm'
import {
  CommercialAgreementStatus,
  ReferralConsentLetterVariant,
} from '@/types/misc'

interface Props {
  fromCompanyName: string
  fromCompanyId: number
  toCompanyId: number
  consentLetterVariant: ReferralConsentLetterVariant
  serviceFees: ServiceFeeDetail[]
  onApprove: () => void
  hasApproved: boolean
  onAmend?: (amendRequest: string) => void
  onClearAction?: () => void
  hasAddedAmendmentRequest: boolean
  amendRequest?: string
  error?: string
  className?: string
  agreementType?: CommercialAgreementTypeOption
  agreementStatus: CommercialAgreementStatus
}

const ReviewESignatureLetterBox: React.FC<Props> = ({
  fromCompanyName,
  fromCompanyId,
  toCompanyId,
  consentLetterVariant,
  serviceFees,
  hasApproved,
  hasAddedAmendmentRequest,
  onApprove,
  onAmend,
  amendRequest,
  error,
  className,
  agreementType,
  agreementStatus,
}) => {
  const { showModal } = useReviewESignatureLetterModal()

  function renderCta() {
    if (hasAddedAmendmentRequest) {
      return (
        <Button variant="success" disabled className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '16px' }} />
          Amendment request added
        </Button>
      )
    }

    if (hasApproved) {
      return (
        <Button variant="success" disabled className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '16px' }} />
          eSignature letter approved
        </Button>
      )
    }

    return (
      <Button
        variant="outline-success"
        onClick={showModal}
        id={DOM_IDS.reviewConsentLetterBox}
      >
        <FontAwesomeIcon icon={faPenField} /> Preview & approve eSignature
        letter
      </Button>
    )
  }

  const hasReviewed = hasApproved || hasAddedAmendmentRequest

  const reviewMessage = (
    <>
      As the FCA-regulated party, you are required to approve the wording of{' '}
      {fromCompanyName}'s introductory letter as a financial promotion. Please
      view and amend / approve as appropriate.
    </>
  )

  return (
    <>
      <Card className={cn('mb-6', className)}>
        <CardContent>
          <CardHeading>Client consent letter</CardHeading>

          <p className="mb-2">{reviewMessage}</p>

          <div className="flex gap-2">
            {renderCta()}

            {hasReviewed && (
              <Button
                variant="plain"
                onClick={showModal}
                style={{ fontSize: '12px' }}
              >
                (View/amend)
              </Button>
            )}
          </div>

          {error && (
            <FieldError className="text-body-base mt-1">{error}</FieldError>
          )}
        </CardContent>
      </Card>

      <ReviewESignatureLetterModal
        title="Approve client eSignature letter"
        fromCompanyId={fromCompanyId}
        toCompanyId={toCompanyId}
        serviceFees={serviceFees}
        agreementType={agreementType}
        agreementStatus={agreementStatus}
        consentLetterVariant={consentLetterVariant}
        renderHeader={() => (
          <>
            <p className="text-md mb-4">{reviewMessage}</p>

            <ReviewActions
              onApprove={onApprove}
              onAmend={onAmend}
              amendRequest={amendRequest}
            />
          </>
        )}
      />
    </>
  )
}

export default ReviewESignatureLetterBox
